import { FOOTSTEP_FOLDER, FOOTSTEP_GAIN } from "./footsteps.ts";

/**
 * The mixer's channels (ADR-085 decision 8, the author's ruling of 2026-10-02): each its own gain under Master, with a
 * volume and a mute the player sets and a voice cap; which channel a hook plays on, from its folder and who acts; and
 * which channel lowers which while it plays. Every rule here is a table, so a channel, a folder or a ducking rule is an
 * edit to data.
 */

/**
 * The channels, by id.
 */
export const Channel = {
  Music: "music",
  Stingers: "stingers",
  Player: "player",
  OtherPlayers: "others",
  Mobs: "mobs",
  Npc: "npc",
  World: "world",
  Ambience: "ambience",
  Ui: "ui",
} as const;
export type Channel = (typeof Channel)[keyof typeof Channel];

/**
 * Who a sound belongs to: the page's own character, another player, a creature, a non-player character, or a thing in
 * the world. Every actor but the page's own character is heard from where it stands.
 */
export const SoundActor = { Own: 0, Player: 1, Mob: 2, Npc: 3, Thing: 4 } as const;
export type SoundActor = (typeof SoundActor)[keyof typeof SoundActor];

/**
 * One channel: its id, its default volume from 0 to 100, and how many of its sounds play at
 * once.
 */
export interface ChannelSpec {
  readonly id: Channel;
  readonly volume: number;
  readonly voices: number;
}

/**
 * The channels in Settings' order.
 */
export const CHANNELS: readonly ChannelSpec[] = Object.freeze([
  { id: Channel.Music, volume: 60, voices: 2 },
  { id: Channel.Stingers, volume: 80, voices: 2 },
  { id: Channel.Player, volume: 80, voices: 8 },
  { id: Channel.OtherPlayers, volume: 70, voices: 8 },
  { id: Channel.Mobs, volume: 80, voices: 8 },
  { id: Channel.Npc, volume: 80, voices: 4 },
  { id: Channel.World, volume: 70, voices: 6 },
  { id: Channel.Ambience, volume: 60, voices: 4 },
  { id: Channel.Ui, volume: 60, voices: 4 },
]);

/**
 * The Master volume a player has before choosing.
 */
export const MASTER_VOLUME = 80;

/**
 * The channel of a folder for each actor, the folder's `channel` for an actor it does not name.
 */
interface FolderChannels {
  readonly folder: string;
  readonly channel: Channel;
  readonly byActor?: Readonly<Partial<Record<SoundActor, Channel>>>;
}

const PEOPLE: Readonly<Partial<Record<SoundActor, Channel>>> = Object.freeze({ [SoundActor.Player]: Channel.OtherPlayers, [SoundActor.Mob]: Channel.Mobs, [SoundActor.Npc]: Channel.Npc });

/**
 * Which channel each folder plays on; the longest folder a hook lies in decides.
 */
export const FOLDER_CHANNELS: readonly FolderChannels[] = Object.freeze([
  { folder: "music", channel: Channel.Music },
  { folder: "stingers", channel: Channel.Stingers },
  { folder: "sfx/player", channel: Channel.Player, byActor: PEOPLE },
  { folder: "sfx/abilities", channel: Channel.Player, byActor: PEOPLE },
  { folder: "sfx/weapons", channel: Channel.Player, byActor: PEOPLE },
  { folder: "sfx/items", channel: Channel.Player, byActor: PEOPLE },
  { folder: "sfx/creatures", channel: Channel.Mobs },
  { folder: "sfx/npcs", channel: Channel.Npc },
  { folder: "sfx/world", channel: Channel.World },
  { folder: "sfx/ui", channel: Channel.Ui },
  { folder: "ambience", channel: Channel.Ambience },
]);

/**
 * One ducking rule: while any sound of `trigger` plays, `target` is lowered by `decibels`, reached over `attack`
 * seconds and released over `release` seconds once the trigger is quiet.
 */
export interface DuckingRule {
  readonly trigger: Channel;
  readonly target: Channel;
  readonly decibels: number;
  readonly attack: number;
  readonly release: number;
}

/**
 * The ducking rules: a stinger lowers the music under it rather than stopping it.
 */
export const DUCKING: readonly DuckingRule[] = Object.freeze([{ trigger: Channel.Stingers, target: Channel.Music, decibels: -12, attack: 0.3, release: 1.5 }]);

/**
 * A channel's spec.
 *
 * @param id The channel.
 * @returns Its spec.
 * @throws Error when the id names no channel, which the types prevent.
 */
export function channelSpec(id: Channel): ChannelSpec {
  const spec = CHANNELS.find((channel) => channel.id === id);
  if (spec === undefined) {
    throw new Error(`${id} names no channel`);
  }
  return spec;
}

/**
 * The channel a hook plays on for an actor.
 *
 * @param hook The hook, or a music folder.
 * @param actor Who the sound belongs to.
 * @returns The channel, or null when the hook lies in no folder of `FOLDER_CHANNELS`.
 */
export function channelOf(hook: string, actor: SoundActor): Channel | null {
  let best: FolderChannels | null = null;
  for (const entry of FOLDER_CHANNELS) {
    if ((hook === entry.folder || hook.startsWith(`${entry.folder}/`)) && (best === null || entry.folder.length > best.folder.length)) {
      best = entry;
    }
  }
  if (best === null) {
    return null;
  }
  return best.byActor?.[actor] ?? best.channel;
}

/**
 * One trim: the hook named `folder`, or every hook in the folder of that name, plays at `gain`, 0 to 1, under its
 * channel's volume.
 */
export interface HookTrim {
  readonly folder: string;
  readonly gain: number;
}

/**
 * The hook of the sword's swish.
 */
export const SWORD_SWISH_HOOK = "sfx/weapons/sword/use";

/**
 * The level the sword's swish plays at under its channel's volume, 0 to 1: about 2.5 dB under the channel's other
 * sounds, since it sounds on every swing of a fight. The sword's hit keeps its folder's level.
 */
export const SWORD_GAIN = 0.75;

/**
 * The hook of the level-up stinger.
 */
export const LEVEL_UP_HOOK = "stingers/player_level_up";

/**
 * The level the level-up stinger plays at under its channel's volume, 0 to 1: 20% under the stingers' loudness
 * target (the author's request of 2026-10-11), about 1.9 dB.
 */
export const LEVEL_UP_GAIN = 0.8;

/**
 * The hook of the chime another player's level-up plays where the page sees it (P2.4-T41, ADR-085's note of 2026-10-24).
 * It lies in `sfx/player`, so it plays on Other players, placed.
 */
export const LEVEL_UP_SEEN_HOOK = "sfx/player/level/level_up";

/**
 * The level the chime plays at under its channel's volume, 0 to 1: half, since a crowd that levels together repeats it
 * and the page's own stinger is the loud moment.
 */
export const LEVEL_UP_SEEN_GAIN = 0.5;

/**
 * The trims (ADR-085 decision 5's "later per-hook trim"): a sound that must sit quieter than its folder's loudness
 * target. A footstep, on every surface, plays at `FOOTSTEP_GAIN`; the sword's swish plays at `SWORD_GAIN`; the
 * level-up stinger plays at `LEVEL_UP_GAIN`; the chime another player's level-up plays at `LEVEL_UP_SEEN_GAIN`.
 */
export const HOOK_TRIMS: readonly HookTrim[] = Object.freeze([
  { folder: FOOTSTEP_FOLDER, gain: FOOTSTEP_GAIN },
  { folder: SWORD_SWISH_HOOK, gain: SWORD_GAIN },
  { folder: LEVEL_UP_HOOK, gain: LEVEL_UP_GAIN },
  { folder: LEVEL_UP_SEEN_HOOK, gain: LEVEL_UP_SEEN_GAIN },
]);

const TRIM_PREFIXES: readonly string[] = HOOK_TRIMS.map((trim) => `${trim.folder}/`);

/**
 * The level a hook plays at under its channel's volume.
 *
 * @param hook The hook.
 * @returns The gain of the first trim that names the hook or a folder the hook lies in, or 1 when none does.
 */
export function hookTrim(hook: string): number {
  for (let index = 0; index < HOOK_TRIMS.length; index += 1) {
    if (hook === (HOOK_TRIMS[index] as HookTrim).folder || hook.startsWith(TRIM_PREFIXES[index] as string)) {
      return (HOOK_TRIMS[index] as HookTrim).gain;
    }
  }
  return 1;
}

/**
 * Whether an actor's sounds are heard from where it stands: everyone's but the page's own character's.
 *
 * @param actor The actor.
 * @returns True when positional.
 */
export function isPositional(actor: SoundActor): boolean {
  return actor !== SoundActor.Own;
}

/**
 * The gain a channel's ducking leaves it: the product of every rule on it whose trigger plays.
 *
 * @param target The channel.
 * @param playing How many sounds a channel plays now.
 * @returns The gain from 0 to 1.
 */
export function duckedGain(target: Channel, playing: (channel: Channel) => number): number {
  let gain = 1;
  for (const rule of DUCKING) {
    if (rule.target === target && playing(rule.trigger) > 0) {
      gain *= 10 ** (rule.decibels / 20);
    }
  }
  return gain;
}
